import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faPenToSquare, faTrashCan, faUpload } from '@fortawesome/free-solid-svg-icons';
import { Role } from '../../components/auth-context/role';
import { useTranslation } from 'react-i18next';
import ImageUploader from '../../components/image-uploader/ImageUploader';
import api from "../../api/api";

export function DocumentationManager() {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        url: '',
        name: '',
        isPublic: false,
    });
    const [editingDocument, setEditingDocument] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                const response = await api.get('/documentation/all');
                if (response?.data) {
                    setDocuments(response.data);
                } else {
                    throw new Error('No data received from server');
                }
                setError(null);
            } catch (error) {
                console.error('Failed to fetch documents:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                });
                const errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to fetch all documents';
                setError(errorMessage);
            }
        };
        fetchDocuments();
    }, []);

    const toggleCreate = () => {
        setShowCreateForm(!showCreateForm);
        setFormData({
            url: '',
            name: '',
            isPublic: false,
        });
        setEditingDocument(null);
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.url || !formData.name) {
            setError('URL and name are required');
            return;
        }
        if (formData.url.length > 255) {
            setError('URL must be 255 characters or less');
            return;
        }
        if (formData.name.length > 100) {
            setError('Name must be 100 characters or less');
            return;
        }

        try {
            const payload = {
                url: formData.url,
                name: formData.name,
                isPublic: formData.isPublic,
            };
            if (editingDocument) {
                const response = await api.patch(`/documentation/${editingDocument.id}`, payload);
                setDocuments(
                    documents.map((doc) => (doc.id === editingDocument.id ? response.data.document : doc))
                );
                alert('Document updated successfully');
            } else {
                const response = await api.post('/documentation', payload);
                setDocuments([...documents, response.data.document]);
                alert('Document created successfully');
            }
            toggleCreate();
        } catch (error) {
            console.error('Failed to save document:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            const errorMessage =
                error.response?.data?.error ||
                error.message ||
                'Failed to save document';
            setError(errorMessage);
        }
    };

    const startEditing = (document) => {
        setEditingDocument(document);
        setFormData({
            url: document.url,
            name: document.name,
            isPublic: document.isPublic,
        });
        setShowCreateForm(true);
        setError(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            try {
                await api.delete(`/documentation/${id}`);
                setDocuments(documents.filter((doc) => doc.id !== id));
                alert('Document deleted successfully');
            } catch (error) {
                console.error('Failed to delete document:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                });
                const errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to delete document';
                setError(errorMessage);
            }
        }
    };

    const handleUploadSuccess = (data) => {
        if (data.urls) {
            // Set the URL in the form
            setFormData(prev => ({
                ...prev,
                url: data.urls.original
            }));
            alert('Document uploaded successfully! Please add a name and save.');
        }
        setShowUploadModal(false);
    };

    const handleUploadError = (error) => {
        alert(`Upload failed: ${error}`);
    };

    return (
        <div className="view-model">
            <h2>{t('documentation-manager')}</h2>
            <Role has="DOC_MANAGER">
                <p>
                    <button
                        className={`btn ${showCreateForm ? 'secondary' : ''}`}
                        onClick={toggleCreate}
                    >
                        <FontAwesomeIcon icon={faCirclePlus} /> {showCreateForm ? t('cancel') : t('create-document')}
                    </button>
                </p>
                {showCreateForm && (
                    <div className="document-form">
                        <h3>{editingDocument ? t('edit-document') : t('create-document')}</h3>
                        {error && <p className="error-message">{error}</p>}
                        <form onSubmit={handleSubmit}>
                            <div>
                                <label>URL:</label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleInputChange}
                                        required
                                        maxLength={255}
                                        placeholder={t('enter-document-url-or-upload-pdf')}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => setShowUploadModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faUpload} /> {t('upload-pdf')}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label>Name:</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={100}
                                    placeholder="Enter document name (max 100 characters)"
                                />
                            </div>
                            <div>
                                <label>
                                    <input
                                        type="checkbox"
                                        name="isPublic"
                                        checked={formData.isPublic}
                                        onChange={handleInputChange}
                                    />
                                    Public
                                </label>
                            </div>
                            <button type="submit" className="btn">
                                {editingDocument ? 'Update Document' : 'Create Document'}
                            </button>
                        </form>
                    </div>
                )}
                <div className="table-container">
                    <table className="pilot-table">
                        <thead>
                        <tr>
                            <th>#ID</th>
                            <th>Name</th>
                            <th>URL</th>
                            <th>Public</th>
                            <th>Created At</th>
                            <th>Updated At</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {documents.length === 0 ? (
                            <tr className="background-change">
                                <td colSpan="7">No documents found.</td>
                            </tr>
                        ) : (
                            documents.map((doc, index) => (
                                <tr key={index} className="background-change">
                                    <td>{doc.id}</td>
                                    <td>{doc.name}</td>
                                    <td>
                                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                            {doc.url}
                                        </a>
                                    </td>
                                    <td>{doc.isPublic ? 'Yes' : 'No'}</td>
                                    <td>{new Date(doc.createdAt).toLocaleString()}</td>
                                    <td>{new Date(doc.updatedAt).toLocaleString()}</td>
                                    <td>
                                        <button className="btn" onClick={() => startEditing(doc)}>
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </button>
                                        <button className="btn danger" onClick={() => handleDelete(doc.id)}>
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </Role>

            {showUploadModal && (
                <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Upload PDF Document</h3>
                            <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <ImageUploader
                                folder="documents"
                                onUploadSuccess={handleUploadSuccess}
                                onUploadError={handleUploadError}
                                maxSizeMB={10}
                                acceptPdf={true}
                                multiple={false}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}